import {
  type PosthogExceptionEntry,
  exceptionValue,
} from "./posthog-exception-types";

export type {
  PosthogExceptionFrame,
  PosthogExceptionEntry,
  ExceptionFrameNameFields,
} from "./posthog-exception-types";
export { exceptionValue, frameNameIncludes } from "./posthog-exception-types";

export function isEmptyExceptionNoise(first: PosthogExceptionEntry): boolean {
  const value = exceptionValue(first).trim();
  if (value !== "") return false;
  const type = (first.type ?? first.$exception_type ?? "").trim();
  if (type !== "") return false;
  const frameCount = first.stacktrace?.frames?.length ?? 0;
  return frameCount === 0;
}

export {
  FACEBOOK_IAB_BRIDGE_FUNCTIONS,
  isFacebookIabBridgeException,
  isFacebookAndroidPerfLoggerNoise,
} from "./facebook-iab-noise";

export {
  isPasswordManagerExtensionNoise,
  isWalletExtensionInjectedScriptPosthog,
  isAdwareWindowInjectionValue,
  isMalwareFetchInjectionPosthog,
} from "./extension-injection-noise";

export {
  isInjectedBrowserBridgeMessage,
  isNextCurrentScriptInvariantValue,
  isFrozenHistoryAssignmentValue,
  isFirefoxIosInjectedGlobalValue,
  isDuckDuckGoFeatureInjectionValue,
  isDuckDuckGoFeatureInjectionPosthog,
  isWebkitAutofillHandlerValue,
} from "./in-app-browser-noise";

export { isGoogleTagManagerInjectedTagNoise } from "./gtm-noise";

export {
  isGoogleMapsRpcTransportNoiseValue,
  isGoogleMapsRpcTransportNoisePosthog,
} from "./google-maps-noise";

export function isRrwebCrossRealmInstanceofPosthog(
  first: PosthogExceptionEntry,
): boolean {
  const value = exceptionValue(first);
  if (!value.includes("Right-hand side of 'instanceof' is not an object")) {
    return false;
  }
  const frames = first.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const file = frame.filename ?? frame.abs_path ?? "";
    return file.includes("rrweb");
  });
}

export function isPostHogLogsInitNoise(first: PosthogExceptionEntry): boolean {
  const value = exceptionValue(first);
  if (!value.includes("Cannot assign to read only property 'debug'")) {
    return false;
  }
  const frames = first.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const file = frame.filename ?? frame.abs_path ?? "";
    return file.includes("static/logs.js");
  });
}

// HACK: PostHog's `static/logs.js` rejects an internal setTimeout with `Operation timed out.` and no `.catch` hook (REPACKIFY-164).
export function isPostHogLogsTimeoutNoise(
  first: PosthogExceptionEntry,
): boolean {
  const value = exceptionValue(first);
  if (!value.includes("Operation timed out")) return false;
  const frames = first.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const file = frame.filename ?? frame.abs_path ?? "";
    return file.includes("static/logs.js");
  });
}

export function isStripeConnectScriptLoadFailureValue(value: string): boolean {
  return (
    value.includes("Failed to load Connect.js") ||
    value.includes("Connect.js did not load the necessary objects")
  );
}

const TRANSLATED_DOM_REMOVE_CHILD_MESSAGE_FRAGMENTS = [
  "Failed to execute 'removeChild' on 'Node'",
  "The node to be removed is not a child of this node",
  "The object can not be found here",
];

export function isTranslatedDomRemoveChildNotFoundError(
  value: string,
  _type?: string,
): boolean {
  if (!value) return false;
  return TRANSLATED_DOM_REMOVE_CHILD_MESSAGE_FRAGMENTS.some((fragment) =>
    value.includes(fragment),
  );
}

const OFFICE_SAFELINKS_PATTERN =
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

export function isOfficeSafeLinksScannerNoiseValue(value: string): boolean {
  return OFFICE_SAFELINKS_PATTERN.test(value);
}

export function isResourceLoadEventNoisePosthog(
  first: PosthogExceptionEntry,
): boolean {
  const value = exceptionValue(first);
  if (
    !value.includes("(type=error)") ||
    !value.includes("captured as promise rejection")
  ) {
    return false;
  }
  return (first.stacktrace?.frames ?? []).length === 0;
}

const RESIZE_OBSERVER_LOOP_FRAGMENTS = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
];

export function isResizeObserverLoopNoise(value: string): boolean {
  if (!value) return false;
  return RESIZE_OBSERVER_LOOP_FRAGMENTS.some((fragment) =>
    value.includes(fragment),
  );
}
